import { camelCase, reduce, snakeCase } from 'lodash';
import { MAX_PICTURE_SIZE_TOTAL, toastMessages } from '../config/constants';
import moment from 'moment';

export const dataTypeEnum = Object.freeze({
  activityData: 'activityData',
  agendaData: 'agendaData',
  bankData: 'bankData',
  clientData: 'clientData',
  companyData: 'companyData',
  documentsData: 'documentsData',
  insuranceData: 'insuranceData',
  placeData: 'placeData',
  userData: 'userData',
});

export function saveData(dataType, data) {
  localStorage.setItem(dataType, JSON.stringify(data));
}

export function getData(dataType) {
  return JSON.parse(localStorage.getItem(dataType));
}

export function clearData() {
  var dataType;
  for (dataType in dataTypeEnum) localStorage.removeItem(dataType);
}

// Dates

export const DAY_NAMES = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
export const DAY_NAMES_NATURAL_ORDER = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

export function getDayNameNaturalOrder(index) {
  return DAY_NAMES_NATURAL_ORDER[index];
}

export function isNumeric(str) {
  if (typeof str != 'string') return false; // we only process strings!
  return (
    !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    !isNaN(parseFloat(str))
  ); // ...and ensure strings of whitespace fail
}

export function getAvailableEmployeeLevelsBySelectedLevel(level) {
  switch (level) {
    case 'beginner':
      return ['beginner', 'intermediate', 'expert'];
    case 'intermediate':
      return ['intermediate', 'expert'];
    case 'expert':
      return ['expert'];
    case 'all':
      return ['beginner', 'intermediate', 'expert'];
    default:
      return ['beginner', 'intermediate', 'expert'];
  }
}

/**
 * Returns `object` with `onAttribute` applied to each one
 * of its attributes
 * @param object the object to go through
 * @param onAttribute function taking the attribute in parameter and returning its new case. This function must return a string.
 * @returns {{}} the object with its attributes case updated.
 */
function attributeToNewCase(object, onAttribute) {
  return reduce(
    object,
    (acc, value, attribute) => {
      return {
        ...acc,
        [onAttribute(attribute)]: value,
      };
    },
    {},
  );
}

export function toCamelCase(object) {
  return attributeToNewCase(object, camelCase);
}

export function toSnakeCase(object) {
  return attributeToNewCase(object, snakeCase);
}

// Activities conflicts
export const haveConflictingHoursErrors = (conflictingHours) => {
  if (conflictingHours.startHour > conflictingHours.endHour) return true;
  return (
    conflictingHours.startHour === conflictingHours.endHour &&
    conflictingHours.startMinute >= conflictingHours.endMinute
  );
};

export const getThumbnailFromFile = (file) => {
  const fileSize = file.size / 1024 / 1024;
  const fileType = file.type.split('/')[0];

  let fileReader = new FileReader();

  return new Promise((resolve) => {
    if (fileType.match('image')) {
      if (fileSize > 8) resolve({ error: toastMessages.error.POST_FILE_SIZE });

      fileReader.onload = () => {
        resolve({ data: file, type: 'image', url: fileReader.result });
      };
      fileReader.readAsDataURL(file);
    } else if (fileType.match('video')) {
      if (fileSize > 100) resolve({ error: toastMessages.error.POST_FILE_SIZE });

      fileReader.onload = () => {
        const blob = new Blob([fileReader.result], { type: file.type });
        const url = URL.createObjectURL(blob);
        let video = document.createElement('video');

        video.addEventListener('loadeddata', () => {
          if (video.duration < 3 || video.duration > 60) resolve({ error: toastMessages.error.VIDEO_DURATION });

          const snap = snapImage();
          if (snap.success) {
            video.pause();
            resolve({ data: file, thumbnail: snap.file, type: snap.type, url: snap.url });
          }
        });

        const snapImage = () => {
          let canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataURL = canvas.toDataURL();
          const success = dataURL.length > 100000;
          if (success) {
            URL.revokeObjectURL(url);

            const image = getImageObjectFromDataURL(dataURL, `${file.name.split('.')[0]}`);
            return { success, file: image, type: 'video', url: dataURL };
          }
          return { success };
        };

        video.preload = 'metadata';
        video.src = url;
        video.muted = true;
        video.playsInline = true;
        video.play();
      };

      fileReader.readAsArrayBuffer(file);
    } else {
      resolve({ error: toastMessages.error.POST_FILE_FORMAT });
    }
  });
};

export const getImageObjectFromDataURL = (dataURL, filename) => {
  let arr = dataURL.split(','),
    mimeType = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);

  while (n--) u8arr[n] = bstr.charCodeAt(n);

  return new File([u8arr], `${filename}.${mimeType.split('/')[1]}`, { type: mimeType });
};

export const createAgendaBody = (events) => {
  return events.map((e) => ({
    id: e.id,
    close_time: moment(e.end).format('HH:mm'),
    day_number: e.day_number,
    employee_id: e.resourceId,
    open_time: moment(e.start).format('HH:mm'),
  }));
};

export const createActivityBody = (values) => {
  let formData = new FormData();

  const employeesAndAddresses = values.employeesAndAddresses.map(({ address, employee }) => ({
    address_id: address.id,
    employee_id: employee.id,
  }));

  formData.append('slotType', values.courseType);
  formData.append('description', values.description);
  formData.append('duration', values.duration);
  formData.append('employeesAndAddresses', JSON.stringify(employeesAndAddresses));
  formData.append('identifier', values.identifier);
  formData.append('level', values.level);
  formData.append('maxAge', values.maxAge);
  formData.append('minAge', values.minAge);
  formData.append('highPrice', values.priceHighSeason);
  formData.append('lowPrice', values.priceLowSeason);
  formData.append('name', values.productName);
  formData.append('clientsNumber', values.slots);
  formData.append('startDate', '2021-02-08');
  formData.append('endDate', '2021-02-09');

  if (values.status) formData.append('status', values.status);

  return formData;
};

export const getTextWidth = (text, font) => {
  const context = document.createElement('canvas').getContext('2d');
  context.font = font;
  return context.measureText(text).width;
};

export const getDtoDateFormat = (dateObject) => {
  return moment(dateObject).format('YYYY-MM-DDTHH:mm:ss');
};

export const checkFileTotalSize = (files = []) => {
  if (!Array.isArray(files)) throw new Error('Parameter must be an array');

  let totalFilesSize = 0;
  for (const file of files) {
    totalFilesSize += file.size;
  }

  return totalFilesSize < MAX_PICTURE_SIZE_TOTAL;
};

export const convertBytesToKiloBytes = (bytes, decimals = 3) => {
  if (!+bytes) return 0;

  const k = 1024;
  return parseFloat((bytes / k).toFixed(decimals));
};

export const checkFileSizeAvailability = (files, maxSize, maxNbFile) => {
  // We check the number of files
  if (maxNbFile < files.length) return 'FILE_MAX_NUMBER';

  // We check the data size
  const filesSize = files.reduce((acc, curr) => acc + curr.size, 0);
  if (maxSize < filesSize) return 'FILE_MAX_DATA';

  return 'OK';
};
